import { motion, useAnimation } from "motion/react";
import {
  HTMLAttributes,
  forwardRef,
  useEffect,
  useImperativeHandle,
} from "react";

import { cn } from "@/lib/utils";

export interface AudioLinesIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface AudioLinesIconProps extends HTMLAttributes<HTMLDivElement> {
  isAnimating?: boolean;
  size?: number;
}

const AnimatedAudioLinesIcon = forwardRef<
  AudioLinesIconHandle,
  AudioLinesIconProps
>(({ className, isAnimating, size = 28, ...props }, ref) => {
  const controls = useAnimation();

  useImperativeHandle(ref, () => {
    return {
      startAnimation: () => controls.start("animate"),
      stopAnimation: () => controls.start("normal"),
    };
  });

  useEffect(() => {
    if (isAnimating === undefined) return;
    controls.start(isAnimating ? "animate" : "normal");
  }, [controls, isAnimating]);

  return (
    <div className={cn(className)} {...props}>
      <svg
        fill="none"
        height={size}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M2 10v3" />
        <motion.path
          initial="normal"
          animate={controls}
          d="M6 6v11"
          variants={{
            normal: { d: "M6 6v11" },
            animate: {
              d: ["M6 6v11", "M6 10v3", "M6 6v11"],
              transition: {
                duration: 1.5,
                repeat: Number.POSITIVE_INFINITY,
              },
            },
          }}
        />
        <motion.path
          initial="normal"
          animate={controls}
          d="M10 3v18"
          variants={{
            normal: { d: "M10 3v18" },
            animate: {
              d: ["M10 3v18", "M10 9v5", "M10 3v18"],
              transition: {
                duration: 1,
                repeat: Number.POSITIVE_INFINITY,
              },
            },
          }}
        />
        <motion.path
          initial="normal"
          animate={controls}
          d="M14 8v7"
          variants={{
            normal: { d: "M14 8v7" },
            animate: {
              d: ["M14 8v7", "M14 6v11", "M14 8v7"],
              transition: {
                duration: 0.8,
                repeat: Number.POSITIVE_INFINITY,
              },
            },
          }}
        />
        <motion.path
          initial="normal"
          animate={controls}
          d="M18 5v13"
          variants={{
            normal: { d: "M18 5v13" },
            animate: {
              d: ["M18 5v13", "M18 7v9", "M18 5v13"],
              transition: {
                duration: 1.5,
                repeat: Number.POSITIVE_INFINITY,
              },
            },
          }}
        />
        <path d="M22 10v3" />
      </svg>
    </div>
  );
});

AnimatedAudioLinesIcon.displayName = "AudioLinesIcon";

export { AnimatedAudioLinesIcon };
